/**
 * Push-to-talk input controller. Listens for the configured PTT key
 * (default `V`) and translates press/release into
 * `VoiceConnection.startSpeaking(mode, target)` /
 * `VoiceConnection.stopSpeaking()` calls.
 *
 * Modifier keys select the voice mode:
 *
 *   - V        → say
 *   - Shift+V  → shout
 *   - Ctrl+V   → whisper (target resolved via `getWhisperTarget` callback)
 *
 * The class is keyboard-aware: PTT presses are ignored while a text
 * input has focus (chat bar, settings inputs) so the user can type
 * the letter `V` without broadcasting.
 *
 * The mic, analyser, and meter live on `VoiceConnection`. This
 * controller owns only input.
 */

import type { VoiceConnection, VoiceMode } from "./VoiceConnection";

export interface PushToTalkOptions {
  pttKey?: string; // default "v" (case-insensitive)
  getWhisperTarget?: () => string | null;
}

export class PushToTalk {
  private pttKey: string;
  private getWhisperTarget: (() => string | null) | null;
  private control: VoiceConnection | null = null;

  private pttHeld = false;
  private currentMode: VoiceMode = "say";

  constructor(options: PushToTalkOptions = {}) {
    this.pttKey = (options.pttKey ?? "v").toLowerCase();
    this.getWhisperTarget = options.getWhisperTarget ?? null;
  }

  setControl(control: VoiceConnection): void {
    this.control = control;
  }

  setWhisperTargetResolver(fn: () => string | null): void {
    this.getWhisperTarget = fn;
  }

  attach(): void {
    window.addEventListener("keydown", this.handleKeyDown);
    window.addEventListener("keyup", this.handleKeyUp);
    window.addEventListener("blur", this.releaseOnBlur);
  }

  destroy(): void {
    window.removeEventListener("keydown", this.handleKeyDown);
    window.removeEventListener("keyup", this.handleKeyUp);
    window.removeEventListener("blur", this.releaseOnBlur);
    if (this.pttHeld) this.release();
  }

  private handleKeyDown = (e: KeyboardEvent): void => {
    if (e.key.toLowerCase() !== this.pttKey) return;
    if (e.repeat) return;
    if (isTypingTarget(e.target)) return;

    e.preventDefault();
    this.pttHeld = true;
    this.currentMode = computeMode(e);

    const target =
      this.currentMode === "whisper" && this.getWhisperTarget
        ? this.getWhisperTarget()
        : null;

    if (this.control) {
      this.control.startSpeaking(this.currentMode, target);
    } else {
      console.warn("[ptt] no control bound — voice_start not pushed");
    }
  };

  private handleKeyUp = (e: KeyboardEvent): void => {
    if (e.key.toLowerCase() !== this.pttKey) return;
    if (!this.pttHeld) return;

    e.preventDefault();
    this.release();
  };

  private releaseOnBlur = (): void => {
    // If the window loses focus while PTT is held the keyup never fires;
    // releasing here prevents a stuck-open mic.
    if (this.pttHeld) this.release();
  };

  private release(): void {
    this.pttHeld = false;
    this.control?.stopSpeaking();
  }
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
}

// PTT modifier → voice mode. Ctrl beats Shift if both are held
// (whisper is the more specific intent).
function computeMode(e: KeyboardEvent): VoiceMode {
  if (e.ctrlKey || e.metaKey) return "whisper";
  if (e.shiftKey) return "shout";
  return "say";
}
